import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AppVisibilityService, ConfigService, ElectronService, NetWorkType } from '..';
import { IPC_CHANNELS, NOTIFICATION_MODE } from '../../../../../electron-utils/electron.model';
import { BrowserNotificationService, IncomingCallNotification } from '../notifications';
import { SESSION_TYPE } from '../sip';
import { Call } from './call.model';

@Injectable({
  providedIn: 'root'
})
export class IncomingCallService {

  private _callNotifications: IncomingCallNotification[] = [];
  public onIncomingCallNotificationsChanged$: Subject<IncomingCallNotification[]> = new Subject();
  public incomingCallNotificationAdded$: Subject<IncomingCallNotification> = new Subject();
  public incomingCallNotificationRemoved$: Subject<number> = new Subject();
  constructor(
    private browserNotification: BrowserNotificationService,
    private electronService: ElectronService,
    private appVisibilityService: AppVisibilityService,
    private configService: ConfigService
  ) {}

  public get callNotifications(): IncomingCallNotification[] {
    return this.callNotifications;
  }

  public addNotification(incomingCall: IncomingCallNotification) {
    this._callNotifications.push(incomingCall);
    this.onIncomingCallNotificationsChanged$.next(this._callNotifications);
    this.incomingCallNotificationAdded$.next(incomingCall);
  }

  public removeNotificationWithIndex(index: number): void {
    const not = this._callNotifications.splice(index, 1)[0];
    if (not.subscription) not.subscription.unsubscribe();
    this.onIncomingCallNotificationsChanged$.next(this._callNotifications);
    this.incomingCallNotificationRemoved$.next(not.id);
  }

  public removeNotificationByCall(call: Call): void {
    const idx = this._callNotifications.findIndex((incoming: IncomingCallNotification) => call.id === incoming.id);
    if (idx !== -1) this.removeNotificationWithIndex(idx);
  }

  public notifyIncomingCall(call: Call): void {
    const mode = this.configService.config?.settings?.notifications?.calls || NOTIFICATION_MODE.ALL;
    if (mode === NOTIFICATION_MODE.DISABLED) return;

    const incomingCallNotification: IncomingCallNotification = {
      id: call.id,
      call: call,
      isInApp: this.appVisibilityService.isVisible
    };

    if (mode === NOTIFICATION_MODE.ALL) {
      if (this.electronService.isElectron) {
        this.openElectronNotification(incomingCallNotification);
      } else if (!incomingCallNotification.isInApp) {
        this.openBrowserNotification(incomingCallNotification);
      }
    }

    this.addNotification(incomingCallNotification);
  }

  private openBrowserNotification(notificationModel: IncomingCallNotification): void {
    const notification: Notification = this.browserNotification.notify(`Incoming call from ${notificationModel.call.fullname}`, {
      body: `${notificationModel.call.number}`,
      badge: 'assets/icons/favicon.png',
      requireInteraction: true
    });

    const cleanup = this.appVisibilityService.onBecomeVisible(() => notification.close());

    notification.addEventListener('click', () => {
      window.focus();
      notification.close();
      cleanup();
    });

    notificationModel.subscription = notificationModel.call.sessionContainer.changeTypeListener.subscribe((v) => {
      if (v === SESSION_TYPE.TERMINATED) {
        notification.close();
        cleanup();
      }
    });
  }

  private openElectronNotification(notificationModel: IncomingCallNotification): void {
    if (typeof notificationModel.id !== "number" || this.appVisibilityService.isVisible) return;

    this.createElectronNotification(notificationModel);

    const cleanup = this.appVisibilityService.onBecomeVisible(() => {
      this.sendMessageToCloseNotification(notificationModel.call.id);
    });

    notificationModel.subscription = notificationModel.call.sessionContainer.changeTypeListener.subscribe((v) => {
      if (v === SESSION_TYPE.TERMINATED) {
        this.sendMessageToCloseNotification(notificationModel.call.id);
        cleanup();
      }
    });
  }

  private createElectronNotification(notificationModel: IncomingCallNotification): void {
    const notification = {
      callId: notificationModel.call.id,
      fullname: notificationModel.call.fullname,
      callee: notificationModel.call.sessionContainer.session.remoteDisplayName.name.local || null,
      networkType: notificationModel.call.networkType || NetWorkType.PTSN
    };
    this.electronService.ipcRenderer.send(IPC_CHANNELS.OPEN_NEW_INCOMING_WINDOW, notification.callId, JSON.stringify(notification));
  }

  private sendMessageToCloseNotification(callId: number) {
    if (this.electronService.isElectron) {
      this.electronService.ipcRenderer.send(IPC_CHANNELS.CLOSE_INCOMING_WINDOW, callId);
    }
  }

  public updateElectronNotification(callId: number, call: Call): void {
    if (this.electronService.isElectron) {
      const notification = {
        callId: call.id,
        fullname: call.fullname,
        callee: call.sessionContainer.session.remoteDisplayName.name.local || null,
        networkType: call.networkType || NetWorkType.PTSN
      };
      this.electronService.ipcRenderer.send(IPC_CHANNELS.UPDATE_INCOMING_CALL, callId, JSON.stringify(notification));
    }
  }
}
